import React, { useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Spinner from 'react-bootstrap/Spinner';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { useForecastApi } from '../../resources/hooks/api/useForecastApiHook';
import { fileDownloadBlob } from '../../resources/functions';

export type ExportKind = 'summary' | 'detailed';

/** <ForecastName>_Summary_2026-11-05.pdf -- today's date in the user's time zone. */
export const exportFileName = (forecastName: string | undefined, kind: ExportKind, on: Date = new Date()): string => {
    const safe = (forecastName || 'Forecast').trim().replace(/[\\/:*?"<>|\r\n\t]+/g, '-').replace(/^[ .-]+|[ .-]+$/g, '') || 'Forecast';
    const day = `${on.getFullYear()}-${String(on.getMonth() + 1).padStart(2, '0')}-${String(on.getDate()).padStart(2, '0')}`;
    return `${safe}_${kind === 'summary' ? 'Summary' : 'Detailed'}_${day}.pdf`;
};

interface Props {
    forecastId: number;
    forecastName?: string;
    /** Year 1-5 shown on the Executive Dashboard page of the PDF. */
    year?: number;
    disabled?: boolean;
    /** Compact button for table rows (the forecast list). */
    small?: boolean;
}

/**
 * "Export PDF" menu on the forecast pages:
 *   Summary  -- the Executive Dashboard (selected year)
 *   Detailed -- dashboard, Five-Year View, Monthly View of each year, Cash Flow, Balance Sheet
 * The PDF is built by gl-api (GET /forecasts/{id}/export) from the same results as the screens.
 */
const ExportPdfMenu: React.FC<Props> = ({ forecastId, forecastName, year = 1, disabled, small }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const { exportPdf } = useForecastApi();
    const [busy, setBusy] = useState<ExportKind | null>(null);

    const run = (kind: ExportKind) => {
        setBusy(kind);
        exportPdf(forecastId, { type: kind, year }, (_m: string, blob: any) => {
            setBusy(null);
            fileDownloadBlob(blob, exportFileName(forecastName, kind));
        }, (msg: string) => {
            setBusy(null);
            toast.error(msg || String(t('export.failed', 'Could not create the PDF. Please try again.')));
        });
    };

    return (
        <Dropdown align="end" className={small ? 'd-inline-block' : undefined}>
            {/* Plain element (not a bootstrap Button) so it looks exactly like the other
                fyf-secondary buttons, e.g. "Recalculate". */}
            <Dropdown.Toggle as="button" type="button" className={`fyf-secondary${small ? ' fyf-sm' : ''}`}
                id={`fyf-export-pdf-${forecastId}`} disabled={disabled || !!busy}>
                {busy ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="far fa-file-pdf me-2" aria-hidden="true" />}
                {busy ? t('export.preparing', 'Preparing PDF…') : t('export.button', 'Export PDF')}
            </Dropdown.Toggle>
            {/* fixed: not clipped by a scrolling table wrapper (forecast list) */}
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item as="button" onClick={() => run('summary')}>
                    <div className="fw-semibold">{t('export.summary', 'Summary')}</div>
                    <small className="text-muted">{t('export.summaryHint', 'Executive Dashboard, Year {{n}}', { n: year })}</small>
                </Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => run('detailed')}>
                    <div className="fw-semibold">{t('export.detailed', 'Detailed')}</div>
                    <small className="text-muted">{t('export.detailedHint', 'Dashboard, five-year and monthly views, cash flow, balance sheet')}</small>
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default ExportPdfMenu;
